import React from "react";
import { useOrganization } from "@clerk/clerk-react";
import { InstallPicker } from "../components/InstallPicker";
import { Toggle } from "../components/Toggle";
import {
  ME_INSIGHTS_BASE,
  ackOnboarding,
  getActivitySessions,
  getBilling,
  getBreakdown,
  getCreditsActivity,
  getMyAlerts,
  getSessions,
  getUsageValue,
  setToggle,
} from "../lib/api";
import type { AuthHeaders } from "../lib/auth";
import type {
  ActivitySession,
  Billing,
  CreditsActivity,
  DateRange,
  Me,
  ToggleKey,
} from "../lib/types";
import { fmtDate, humanize, toNumber } from "../lib/format";
import { denseLinked } from "../lib/onboarding";
import {
  Callout,
  Button,
  Card,
  EmptyState,
  Grid,
  IconTile,
  LinkButton,
  Pill,
  RowButton,
  SetupSteps,
  Skeleton,
  Sparkline,
  StatTile,
  Table,
  Tooltip,
  type SetupStep,
} from "../design";
import { shortRepo, timeOf, usdShort, useLoad } from "./insights/util";
import { SessionTimeline } from "./insights/ActivityPanel";
import { MONEY_SAVED_TIP } from "./insights/Savings";
import { SPEND_TIP } from "./insights/SpendExplorer";
import { LITE_TIP } from "./insights/Glossary";
import { RunwayNote, runwayDays } from "./Credits";
import { HowCondenseHelped } from "./insights/Helped";
import { resetLabel } from "./insights/value";

function CompressionCta({
  trialAvailable,
  paymentFailed,
  credited,
}: {
  trialAvailable: boolean;
  paymentFailed: boolean;
  // Billed from credits: there is no plan or trial to offer.
  credited: boolean;
}) {
  return (
    <Notice
      title="Nothing is being compressed yet"
      href={credited ? "#credits" : "#billing?plans=1"}
      body={`Your requests reach your provider at full size, so there is nothing saved to show. ${
        credited
          ? "Add credits to turn compression on."
          : paymentFailed
            ? "We couldn't collect your last payment. Update your payment method to resume compression."
            : trialAvailable
              ? "Pick a plan to start its 14-day free trial; nothing is charged until it ends."
              : "Choose a plan to turn compression on."
      }`}
      action={
        credited
          ? "Add credits"
          : paymentFailed
            ? "Update payment method"
            : trialAvailable
              ? "Choose a trial plan"
              : "Choose a plan"
      }
    />
  );
}

export function PoolSpentNotice({ billing }: { billing: Billing }) {
  const refill = billing.week_resets_at
    ? ` until it refills on ${fmtDate(billing.week_resets_at)}`
    : "";
  return (
    <Notice
      warn
      title="Weekly pool spent: on Condense Lite"
      body={`Condense Lite keeps compressing at up to 20% of each prompt${refill}.`}
      action="See plans"
    />
  );
}

function Notice({
  title,
  body,
  action,
  warn = false,
  icon,
  href = "#billing",
}: {
  title: string;
  body: string;
  action?: string;
  warn?: boolean;
  icon?: React.ReactNode;
  href?: string;
}) {
  return (
    <Callout
      tone={warn ? "warn" : "info"}
      icon={icon ?? (warn ? "alert" : "sparkle")}
      title={title}
      action={
        action && (
          <LinkButton size="sm" href={href} iconRight="arrow-right">
            {action}
          </LinkButton>
        )
      }
    >
      {body}
    </Callout>
  );
}

function SeatNotice({ orgName }: { orgName: string | null }) {
  return (
    <Notice
      icon={<IconTile icon="users" tone="violet" />}
      title={`No seat in ${orgName || "your organization"}`}
      body="The organization plan covers seat holders only, so you're compressing on a one-time welcome allowance. Ask an org admin to assign you a seat."
    />
  );
}

/** Reads the balance from the Credits page's own source so the two agree. */
function Credits({ me, authHeaders }: { me: Me; authHeaders: AuthHeaders }) {
  const enabled = !!me.credits_enabled;
  const [activity, setActivity] = React.useState<
    CreditsActivity | null | undefined
  >(undefined);
  React.useEffect(() => {
    if (!enabled) return;
    let live = true;
    getCreditsActivity(authHeaders).then(
      (a) => live && setActivity(a.available ? a : null),
      () => live && setActivity(null),
    );
    return () => {
      live = false;
    };
  }, [authHeaders, enabled, me.credit_balance]);
  if (!enabled) return null;
  if (activity === undefined)
    return (
      <div className="ds-stat" aria-busy>
        <Skeleton lines={3} />
      </div>
    );
  const balance = toNumber(activity ? activity.balance : me.credit_balance);
  const empty = balance <= 0;
  const cap = me.signup_intent === "org" ? toNumber(me.credit_cap) : 0;
  const days = activity ? runwayDays(activity) : null;
  return (
    <StatTile
      label="Credit balance"
      info="Credits pay condense's own fee for compressing your traffic. Your model provider still bills its tokens to you directly; that is Spend."
      value={usdShort(Math.max(balance, 0))}
      note={
        empty ? (
          "Out of credits, now on Condense Lite"
        ) : days !== null ? (
          <RunwayNote days={days} />
        ) : cap > 0 ? (
          `left of ${usdShort(cap)} cap`
        ) : (
          "for compression compute"
        )
      }
      onClick={() => {
        window.location.hash = "credits";
      }}
    />
  );
}

/** No request has gone through condense on this account yet. */
export function hasNoTraffic(me: Me): boolean {
  return !me.usage?.request_count && !me.usage?.tokens_saved;
}

export type SetupStatus = "waiting" | "linked" | "running" | "connected";

/** How far a new account has got: CLI signed in, dense running, first request. */
export function setupStatus({
  linked,
  running,
  traffic,
}: {
  linked: boolean;
  running: boolean;
  traffic: boolean;
}): SetupStatus {
  if (traffic) return "connected";
  if (running) return "running";
  if (linked) return "linked";
  return "waiting";
}

function GetStarted({
  me,
  authHeaders,
  install,
  plan,
}: {
  me: Me;
  authHeaders: AuthHeaders;
  install: React.ReactNode;
  /** A plan or credits still to pick: shown as the first step. */
  plan?: { href: string; label: string; sub: string } | null;
}) {
  // dense reports a session before the agent sends anything, so a session is
  // the first sign that the install worked and the agent is running.
  const [running, setRunning] = React.useState(false);
  React.useEffect(() => {
    if (running) return;
    let live = true;
    let timer: number | null = null;
    const tick = () =>
      getSessions(authHeaders)
        .then(
          (d) =>
            live && (d.active.length || d.history.length) && setRunning(true),
          () => {},
        )
        .finally(() => {
          if (live) timer = window.setTimeout(tick, 5000);
        });
    void tick();
    return () => {
      live = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [authHeaders, running]);
  const status = setupStatus({
    linked: denseLinked(me),
    running,
    traffic: false,
  });
  const steps: SetupStep[] = [
    ...(plan
      ? [
          {
            key: "plan",
            title: "Pick a plan",
            sub: plan.sub,
            action: (
              <LinkButton variant="primary" size="sm" href={plan.href}>
                {plan.label}
              </LinkButton>
            ),
          },
        ]
      : []),
    {
      key: "install",
      title: "Install the CLI",
      sub: "Run this in a terminal. It installs dense and signs it in to this account.",
      detail: install,
      done: status !== "waiting",
    },
    {
      key: "run",
      title: "Start your agent through dense",
      sub: (
        <>
          Run <code>dense claude</code>, <code>dense codex</code> or{" "}
          <code>dense opencode</code> in any project.
        </>
      ),
      done: status === "running",
      waiting: status === "linked",
    },
    {
      key: "first",
      title: "First request reaches condense",
      sub: "This page updates by itself. Nothing after a few minutes? Check that the agent was started with dense in front.",
      waiting: status === "running",
    },
  ];
  return (
    <Card
      title="Get started"
      info="condense sits between your coding agent and the model provider, trims what the agent sends, and shows what it costs."
      actions={
        <Pill tone="info" icon="clock">
          Waiting for your first request
        </Pill>
      }
    >
      <SetupSteps steps={steps} />
    </Card>
  );
}

function PlanAllowance({ billing }: { billing: Billing }) {
  const cap = Math.max(0, billing.week_cap);
  const used = Math.max(0, billing.week_saved);
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const dry = cap > 0 && used >= cap;
  // Pool spent but Condense Lite is still compressing under its ceiling — a
  // capped lane on the same plan, so it never reads as the empty/danger state.
  const lite = billing.lane === "lite";
  const laneSaved = Math.max(0, billing.lane_saved ?? 0);
  // Seatless org member: /me/billing answers with the one-time org welcome
  // grant, so the gauge is real; the label says which allowance this is.
  const unseated = billing.org && billing.seat_assigned === false;
  const orgTrial = billing.org && !unseated && billing.is_trial;
  const oneTime = unseated || orgTrial;
  const label = unseated
    ? "Welcome allowance"
    : billing.is_trial
      ? "Trial allowance"
      : "Plan allowance";
  const resets = resetLabel(billing.week_resets_at, Date.now());
  const liteResumes = resets
    ? `, full rate ${resets.replace(/^resets /, "")}`
    : "";
  const sub = lite
    ? (laneSaved > 0
        ? `${humanize(laneSaved)} saved on Lite`
        : "Still compressing on Lite") + liteResumes
    : dry
      ? oneTime
        ? "Grant used up, now on Condense Lite"
        : "Weekly cap reached, now on Condense Lite"
      : resets && !oneTime && !billing.is_trial
        ? `${humanize(used)} of ${humanize(cap)} tokens this week · ${resets}`
        : billing.is_trial && billing.trial_ends_at
          ? `Trial ends ${fmtDate(billing.trial_ends_at)}`
          : `${humanize(used)} of ${humanize(cap)} tokens`;
  return (
    <StatTile
      label={label}
      info={`condense's own allowance for full-strength compression${oneTime ? "" : " this week"}, counted in tokens it took off. It is separate from your Claude or ChatGPT plan's limits, and the date range doesn't change it. Once it is used up, Condense Lite keeps compressing at up to 20%.`}
      value={`${pct}% used`}
      delta={
        lite && (
          <Tooltip text={LITE_TIP}>
            <Pill tone="info">Lite</Pill>
          </Tooltip>
        )
      }
      note={sub}
      spark={
        <span
          className="per-meter"
          role="meter"
          aria-label="Share of the plan allowance used"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
        >
          <span
            style={{
              width: `${pct}%`,
              background: dry && !lite ? "var(--ds-bad)" : "var(--ds-s1)",
            }}
          />
        </span>
      }
    />
  );
}

export function PersonalSummary({
  me,
  authHeaders,
  refresh,
  range,
  onTab,
  limits,
}: {
  me: Me;
  authHeaders: AuthHeaders;
  refresh: () => Promise<void>;
  range: DateRange;
  onTab: (tab: "spend" | "sessions" | "savings") => void;
  /** Budgets that cap this person's spend, shown under the headline tiles. */
  limits?: React.ReactNode;
}) {
  const usage = me.usage;
  const tokensSaved = usage ? usage.tokens_saved : 0;
  const moneyNum = toNumber(usage?.money_saved);

  // On reload Clerk hydrates with no active org and only then (re)activates
  // the workspace (WorkspaceSwitcher force-activates a lone membership).
  // Tokens minted in that gap are personal, so /me/billing would answer for
  // the wrong account — hold fetches until the context settles, and drop
  // any billing response whose org flag disagrees with the active workspace.
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const activeOrgId = organization?.id ?? null;
  const orgPending =
    !orgLoaded || (!activeOrgId && (me.organizations?.length ?? 0) === 1);

  const [billing, setBilling] = React.useState<Billing | null | undefined>(
    undefined,
  );
  React.useEffect(() => {
    setBilling(undefined);
  }, [activeOrgId]);
  // Refetch in lockstep with the parent /me poll: each tick replaces `me`,
  // and tokens_saved moving is exactly when the allowance changes.
  React.useEffect(() => {
    if (orgPending) return;
    let cancelled = false;
    getBilling(authHeaders)
      .then((b) => {
        if (cancelled) return;
        if (b.enabled && b.org !== (activeOrgId !== null)) return;
        setBilling(b);
      })
      .catch(() => !cancelled && setBilling(null));
    return () => {
      cancelled = true;
    };
  }, [authHeaders, me.usage?.tokens_saved, orgPending, activeOrgId]);
  const compressing = !!billing?.compressing;
  const personal = !!billing?.enabled && !billing.org;
  const poolSpent =
    personal &&
    (billing?.week_cap ?? 0) > 0 &&
    (billing?.lane === "lite" || !compressing);
  const showCompressionCta = personal && !compressing && !poolSpent;

  const fresh = tokensSaved === 0;
  const noTraffic = hasNoTraffic(me);
  // Seen empty on this visit: when the first request lands, say so once.
  const [sawEmpty] = React.useState(noTraffic);
  const [connectedSeen, setConnectedSeen] = React.useState(false);
  const install = (
    <InstallPicker
      seen={me.onboarding?.install_explainer_seen ?? false}
      onSeen={async () => {
        try {
          await ackOnboarding(authHeaders, "install_explainer_seen");
        } catch {
          // best-effort; the modal still shows this session
        }
        await refresh();
      }}
    />
  );
  const [open, setOpen] = React.useState<ActivitySession | null>(null);
  const { data: mine } = useLoad(
    () =>
      Promise.all([
        getBreakdown(authHeaders, ME_INSIGHTS_BASE, range, "none"),
        getBreakdown(
          authHeaders,
          ME_INSIGHTS_BASE,
          range,
          "cost_basis",
          {},
          "saved_usd",
        ),
        getActivitySessions(authHeaders, ME_INSIGHTS_BASE, range).catch(
          () => [] as ActivitySession[],
        ),
        getMyAlerts(authHeaders).catch(() => null),
      ]),
    [authHeaders, range.start, range.end, noTraffic],
  );
  const [spendB, savedB, sessions, alerts] = mine ?? [null, null, [], null];
  const daily = (b: typeof spendB) =>
    (b?.series ?? []).map((d) =>
      Object.values(d.values).reduce((a, v) => a + Number(v), 0),
    );
  const dailySpend = daily(spendB);
  const dailySaved = daily(savedB);
  const { data: value, loading: valueLoading } = useLoad(
    () => getUsageValue(authHeaders, range),
    [authHeaders, range.start, range.end, noTraffic],
  );
  const expensive = [...sessions]
    .sort((a, b) => Number(b.cost_usd) - Number(a.cost_usd))
    .slice(0, 3);
  const alertsUnset = !!alerts && !alerts.session_usd && !alerts.month_usd;
  const allowance =
    billing === undefined ? (
      <div className="ds-stat" aria-busy key="allowance">
        <Skeleton lines={3} />
      </div>
    ) : billing?.tier ? (
      <PlanAllowance billing={billing} key="allowance" />
    ) : me.credits_enabled ? (
      <Credits me={me} authHeaders={authHeaders} key="allowance" />
    ) : null;
  const tiles = [
    <StatTile
      key="spend"
      label="Spend at API prices"
      info={SPEND_TIP}
      value={spendB ? usdShort(spendB.totals.cost_usd) : "—"}
      spark={
        dailySpend.length > 1 &&
        dailySpend.some((v) => v > 0) && (
          <Sparkline
            values={dailySpend}
            color="var(--ds-s1)"
            label="Spend per day"
          />
        )
      }
      onClick={() => onTab("spend")}
    />,
    <StatTile
      key="saved"
      label="Money saved"
      info={MONEY_SAVED_TIP}
      value={spendB ? usdShort(spendB.totals.saved_usd) : usdShort(moneyNum)}
      note={fresh ? "Nothing measured yet" : undefined}
      spark={
        dailySaved.length > 1 &&
        dailySaved.some((v) => v > 0) && (
          <Sparkline
            values={dailySaved}
            color="var(--ds-saved)"
            label="Money saved per day"
          />
        )
      }
      onClick={() => onTab("savings")}
    />,
    allowance,
  ].filter(Boolean);

  return (
    <div className="per-summary">
      {poolSpent && billing && <PoolSpentNotice billing={billing} />}
      {showCompressionCta && !noTraffic && (
        <CompressionCta
          trialAvailable={!!billing?.trial_available}
          paymentFailed={billing?.status === "unpaid"}
          credited={!!billing?.sub_credited}
        />
      )}
      {!!billing?.enabled && billing.org && billing.seat_assigned === false && (
        <SeatNotice orgName={organization?.name ?? null} />
      )}
      {noTraffic && (
        <GetStarted
          me={me}
          authHeaders={authHeaders}
          install={install}
          plan={
            showCompressionCta
              ? billing?.sub_credited
                ? {
                    href: "#credits",
                    label: "Add credits",
                    sub: "Credits pay condense's fee; nothing is compressed until you add some.",
                  }
                : {
                    href: "#billing?plans=1",
                    label: "Choose a plan",
                    sub: billing?.trial_available
                      ? "Every plan starts with a 14-day free trial; nothing is charged until it ends."
                      : "Compression starts once you are on a plan.",
                  }
              : null
          }
        />
      )}
      <>
        {!noTraffic && sawEmpty && !connectedSeen && (
          <Callout
            tone="ok"
            role="status"
            title="condense is connected"
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConnectedSeen(true)}
              >
                Dismiss
              </Button>
            }
          >
            Your first request came through. Spend and savings fill in below as
            you work.
          </Callout>
        )}
        <Grid cols={tiles.length > 2 ? 3 : 2}>{tiles}</Grid>
        {limits}
        <HowCondenseHelped value={value} loading={valueLoading} />
        <Card
          flush
          title="Highest-usage sessions"
          info="Session usage at API prices. Subscription usage is covered by your plan; it is not an additional charge."
          actions={
            <>
              {alertsUnset && (
                <Tooltip text="Get an email when one session, or this month's spend, passes a line you choose.">
                  <LinkButton
                    variant="link"
                    size="sm"
                    icon="mail"
                    href="#account?tab=notifications"
                  >
                    Set an alert
                  </LinkButton>
                </Tooltip>
              )}
              <Button
                variant="link"
                size="sm"
                iconRight="arrow-up-right"
                onClick={() => onTab("sessions")}
              >
                All sessions
              </Button>
            </>
          }
        >
          {expensive.length === 0 ? (
            <EmptyState
              compact
              title={
                noTraffic
                  ? "Your sessions show up here"
                  : "No sessions in this range"
              }
            />
          ) : (
            <Table label="Highest-usage sessions at API prices">
              <tbody>
                {expensive.map((x) => (
                  <RowButton
                    key={x.id}
                    onOpen={() => setOpen(x)}
                    label={`Open session in ${x.repo ? shortRepo(x.repo) : "no repo"}, ${usdShort(x.cost_usd)}`}
                  >
                    <td>
                      <span className="strong">
                        {x.repo ? shortRepo(x.repo) : "No repo"}
                      </span>
                      <span className="ds-cell-sub">
                        {[x.branch, timeOf(x.started_at)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </td>
                    <td className="num">
                      <strong>{usdShort(x.cost_usd)}</strong>
                      <span className="ds-cell-sub">At API prices</span>
                    </td>
                  </RowButton>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </>

      {open && (
        <SessionTimeline
          authHeaders={authHeaders}
          base={ME_INSIGHTS_BASE}
          session={open}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** The proxy switches and the install command: personal settings, kept on
 * the Account page rather than the usage summary. */
export function AgentControls({
  me,
  authHeaders,
  refresh,
}: {
  me: Me;
  authHeaders: AuthHeaders;
  refresh: () => Promise<void>;
}) {
  const [busy, setBusy] = React.useState<ToggleKey | null>(null);
  const [toggleError, setToggleError] = React.useState<string | null>(null);
  async function flip(key: ToggleKey, next: boolean) {
    setToggleError(null);
    setBusy(key);
    try {
      await setToggle(authHeaders, key, next);
      await refresh();
    } catch (e) {
      setToggleError(
        `Couldn't change ${key.replace(/_/g, " ")}: ${(e as Error).message}`,
      );
    } finally {
      setBusy(null);
    }
  }

  const [zdrBusy, setZdrBusy] = React.useState(false);
  const zdrEnforced = !!me.toggles?.zdr_enforced;
  const zdrOn = zdrEnforced || !!me.toggles?.zdr;
  async function flipZdr(next: boolean) {
    setZdrBusy(true);
    try {
      await setToggle(authHeaders, "zdr", next);
      await refresh();
    } catch (e) {
      setToggleError(
        `Couldn't change zero data retention: ${(e as Error).message}`,
      );
    } finally {
      setZdrBusy(false);
    }
  }

  const intercepts: Array<{ key: ToggleKey; label: string; desc: string }> = [
    {
      key: "intercept_condense_compact",
      label: "/condense-compact",
      desc: "Your /compact command summarised by condense.",
    },
    {
      key: "auto_condense",
      label: "auto-condense",
      desc: "Continuous background compaction of the conversation as it grows.",
    },
  ];
  const fresh = hasNoTraffic(me);
  const install = (
    <InstallPicker
      seen={me.onboarding?.install_explainer_seen ?? false}
      onSeen={async () => {
        try {
          await ackOnboarding(authHeaders, "install_explainer_seen");
        } catch {
          // best-effort; the modal still shows this session
        }
        await refresh();
      }}
    />
  );
  return (
    <Card
      title="Controls"
      info="On: condense handles it. Off: requests go straight to the provider."
    >
      {toggleError && (
        <div className="per-err" role="alert">
          {toggleError}
        </div>
      )}
      <div className="per-rows">
        {intercepts.map((row) => (
          <label key={row.key} className="per-row">
            <span className="per-row-text">
              <span className="per-row-title mono">{row.label}</span>
              <span className="per-row-sub">{row.desc}</span>
            </span>
            <Toggle
              checked={!!me.toggles?.[row.key]}
              disabled={busy === row.key}
              onChange={(next) => flip(row.key, next)}
              ariaLabel={row.label}
            />
          </label>
        ))}
        <label className="per-row">
          <span className="per-row-text">
            <span className="per-row-title">Zero data retention</span>
            <span className="per-row-sub">
              {zdrEnforced
                ? "On for everyone in your organization: conversation content is never written to storage, only billing metadata is kept."
                : "On: conversation content is never written to storage, only billing metadata is kept. If your organization turns it on, it applies to you too."}
            </span>
          </span>
          <Toggle
            checked={zdrOn}
            disabled={zdrBusy || zdrEnforced}
            onChange={(next) => flipZdr(next)}
            ariaLabel="Zero data retention"
          />
        </label>
      </div>
      {!fresh && (
        <div className="per-install-more">
          <div className="per-install-head">
            <span className="per-row-title">Install on another machine</span>
            <LinkButton
              variant="link"
              size="sm"
              href="/?setup=1"
              iconRight="arrow-up-right"
            >
              Setup guide
            </LinkButton>
          </div>
          {install}
        </div>
      )}
    </Card>
  );
}
